import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import {
  BalanceWithDeltaCell,
  BenefitAmountCell,
  CostCell,
  CumulativeDrawnCell,
  LtcPoolWithDeltaCell,
  PaidCell,
  PoolLimitCell,
  WhenCell,
} from './cells';
import { currency, monthDisplay } from './formatters';
import { GrowthFactor, HeaderTooltip } from './HeaderTooltip';
import type { StrategyPresentation, UniversalColumnKey } from './strategies/types';
import type { ColumnGroup, PoolDrawdownToDate } from './types';

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const avg = (xs: number[]) => (xs.length === 0 ? 0 : sum(xs) / xs.length);

const DEFAULT_POOL_TOOLTIP = (
  <HeaderTooltip
    description="End-of-month LTC pool balance, with this month's change beneath."
    formulas={[
      { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
      { label: 'Δ shown', expression: 'closing − opening' },
    ]}
  />
);

const DEFAULT_PAYOUT_COLUMN_TOOLTIP = (
  <HeaderTooltip
    description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses."
    formulas={[
      { label: 'Headline', expression: 'this month\'s payout' },
      { label: 'Cumulative', expression: 'Σ monthly payouts through this month' },
    ]}
  />
);

// Shared disclaimer surfaced wherever Care cost is displayed. The asterisk on
// the column label points here; per-strategy cost tooltips also reuse this
// note so the meaning is identical everywhere.
export const PROJECTED_COST_NOTE = (
  <>
    <span className="font-semibold text-amber-300">*</span> Care cost is{' '}
    <span className="italic">projected</span>. Waterlily uses statistical growth modeling on
    your demographics, care preferences, and current health to estimate care duration, environment,
    and unit costs. Actual costs may vary.
  </>
);

const DEFAULT_COST_TOOLTIP = (
  <HeaderTooltip
    description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
    formulas={[
      {
        label: 'Daily cost',
        expression: (
          <>
            today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
          </>
        ),
      },
      {
        label: 'Monthly cost',
        expression: 'daily cost × days in month',
      },
    ]}
    notes={PROJECTED_COST_NOTE}
  />
);

const DEFAULT_BENEFIT_AMOUNT_TOOLTIP = (
  <HeaderTooltip
    description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath."
    formulas={[
      {
        label: 'Monthly max',
        expression: (
          <>
            at-issue daily × days in month × <GrowthFactor rate="inflation rate" exponent="policy year − 1" />
          </>
        ),
      },
    ]}
    notes="Capped at the inflation cap year if the rider specifies one. Simple-interest riders use (1 + rate × years) instead of compounding."
  />
);

const DEFAULT_POOL_LIMIT_TOOLTIP = (
  <HeaderTooltip
    description={
      <>
        Inflated LTC pool size at this policy year if no benefits were used. Pools keep growing on each
        future anniversary until any inflation cap is reached. Distinct from{' '}
        <span className="italic">Pool remaining</span>, which is the actual balance after payouts.
      </>
    }
    formulas={[
      {
        label: 'Per pool',
        expression: (
          <>
            at-issue pool × <GrowthFactor rate="pool inflation rate" exponent="policy year − 1" />
          </>
        ),
      },
      {
        label: 'Total',
        expression: 'Σ per-pool values (AOB + COB)',
      },
    ]}
    notes="Each pool is capped at its own inflation cap year if specified — once capped, the value is a true ceiling."
  />
);

const DEFAULT_DRAWN_TOOLTIP = (
  <HeaderTooltip
    description="Cumulative reduction of the LTC pool through this month — any utilization before the projection plus Acceleration (AOB) and Continuation (COB) drawdown."
    formulas={[
      { label: 'Drawn', expression: 'prior utilization + Σ (monthly pool depletion)' },
      { label: 'Identity', expression: 'Pool limit − Drawn = Pool remaining (while pool is open)' },
    ]}
    notes="Reflects pool drawdown, which can be less than cash paid (indemnity / rider-on-top)."
  />
);

const DEFAULT_DEATH_BENEFIT_TOOLTIP = (
  <HeaderTooltip
    description="End-of-month death benefit, with this month's change beneath. Closes equal to the next month's opening."
    formulas={[
      {
        label: 'Death benefit',
        expression: 'at-issue Face Amount − Σ (qualifying drawdown)',
      },
      {
        label: 'Floor',
        expression: 'max(above, Residual / minimum guaranteed)',
      },
    ]}
  />
);

/** Column set for the post-payout "After benefits begin" table. */
export function buildPayoutColumnGroups(opts: {
  terms: ClaimTerms;
  yearSnapshots: YearSnapshot[];
  presentation: StrategyPresentation;
  poolDrawdown: PoolDrawdownToDate;
}): ColumnGroup[] {
  const { yearSnapshots, terms, presentation, poolDrawdown } = opts;
  const snapshotByYear = new Map(yearSnapshots.map(s => [s.policyYear, s]));

  const universalGroups: ColumnGroup[] = [
    {
      title: 'Timeline',
      columns: [
        {
          key: 'when',
          label: 'Policy month',
          tooltip: 'Projection month, policy year, and client age.',
          sticky: true,
          render: ({ month }) => <WhenCell month={month} />,
          renderAggregate: ({ months, label }) => {
            const start = monthDisplay(months[0].context.monthIndex);
            const end = monthDisplay(months[months.length - 1].context.monthIndex);
            const range = start === end ? `Month ${start}` : `Months ${start}–${end}`;
            return (
              <div className="leading-tight">
                <div className="text-sm font-semibold text-gray-900">{label}</div>
                <div className="text-[10px] text-gray-500">
                  {range} · {months.length} mo
                </div>
              </div>
            );
          },
        },
      ],
    },
    {
      title: 'This month',
      columns: [
        {
          key: 'cost',
          label: 'Care cost',
          labelSuffix: (
            <sup className="ml-0.5 font-normal text-gray-500" aria-label="projected">*</sup>
          ),
          tooltip: presentation.copy?.costTooltip ?? DEFAULT_COST_TOOLTIP,
          render: ({ month }) => <CostCell month={month} terms={terms} />,
          renderAggregate: ({ months, kind }) => {
            const values = months.map(m => m.facts.effectiveCareCost);
            const isTotal = kind === 'total';
            return (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">
                  {currency(isTotal ? sum(values) : avg(values))}
                </div>
                <div className="text-[10px] text-gray-500">
                  {isTotal ? 'total' : 'avg/mo'}
                </div>
              </div>
            );
          },
        },
        {
          key: 'benefit-amount',
          label: 'Benefit cap',
          tooltip: presentation.copy?.benefitAmountTooltip ?? DEFAULT_BENEFIT_AMOUNT_TOOLTIP,
          render: ({ month }) => {
            const snap = snapshotByYear.get(month.context.policyYear);
            const strategyCell = presentation.benefitAmountCell?.({
              month,
              snapshot: snap,
              terms,
            });
            if (strategyCell) return strategyCell;
            if (!snap) return <span className="text-gray-300">—</span>;
            return <BenefitAmountCell snapshot={snap} terms={terms} presentation={presentation} />;
          },
          renderAggregate: ({ months }) => {
            const values = months
              .map(m => {
                const snap = snapshotByYear.get(m.context.policyYear);
                const strategyValue = presentation.benefitAmountValueForMonth?.({
                  month: m,
                  snapshot: snap,
                  terms,
                });
                return strategyValue ?? snap?.monthlyBenefitCap;
              })
              .filter((v): v is number => v !== undefined && v !== null);
            if (values.length === 0) return <span className="text-gray-300">—</span>;
            return (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">{currency(avg(values))}</div>
                <div className="text-[10px] text-gray-500">avg/mo</div>
              </div>
            );
          },
        },
      ],
    },
    {
      title: 'This month',
      columns: [
        {
          key: 'payout',
          label: 'Policy payout',
          tooltip: presentation.copy?.payoutColumnTooltip ?? DEFAULT_PAYOUT_COLUMN_TOOLTIP,
          render: ({ month, cumulativeLtcPayoutToDate }) => (
            <PaidCell
              month={month}
              cumulativeToDate={cumulativeLtcPayoutToDate}
              presentation={presentation}
              terms={terms}
            />
          ),
          renderAggregate: ({ cumulativeLtcPayoutAtEnd }) => (
            <div className="leading-tight">
              <div className="font-medium text-gray-900">
                {currency(cumulativeLtcPayoutAtEnd)}
              </div>
              <div className="text-[10px] text-gray-500">paid to date</div>
            </div>
          ),
        },
      ],
    },
    {
      title: 'Running balances',
      columns: [
        {
          key: 'pool',
          label: 'Pool remaining',
          tooltip: presentation.copy?.poolTooltip ?? DEFAULT_POOL_TOOLTIP,
          render: ({ month, openState }) => (
            <LtcPoolWithDeltaCell
              openState={openState}
              closeState={month.state}
              tooltipContent={
                presentation.poolCellTooltip?.({ month, openState, terms }) ?? undefined
              }
            />
          ),
          renderAggregate: ({ openStateAtStart, closeStateAtEnd, kind }) => (
            <BalanceWithDeltaCell
              opening={
                kind === 'total' ? getLtcPool(closeStateAtEnd) : getLtcPool(openStateAtStart)
              }
              closing={getLtcPool(closeStateAtEnd)}
            />
          ),
        },
        {
          key: 'pool-limit',
          label: 'Pool limit',
          tooltip: presentation.copy?.poolLimitTooltip ?? DEFAULT_POOL_LIMIT_TOOLTIP,
          render: ({ month }) => (
            <PoolLimitCell snapshot={snapshotByYear.get(month.context.policyYear)} terms={terms} />
          ),
          renderAggregate: ({ months }) => (
            <PoolLimitCell
              snapshot={snapshotByYear.get(months[months.length - 1]?.context.policyYear)}
              terms={terms}
            />
          ),
        },
        {
          key: 'pool-drawn',
          label: 'Drawn to date',
          tooltip: presentation.copy?.drawnTooltip ?? DEFAULT_DRAWN_TOOLTIP,
          render: ({ month }) => (
            <CumulativeDrawnCell
              drawn={poolDrawdown.byMonthIndex.get(month.context.monthIndex)}
              priorUtilization={poolDrawdown.priorUtilization}
              labels={presentation.drawnCellLabels}
            />
          ),
          renderAggregate: ({ months }) => (
            <CumulativeDrawnCell
              drawn={poolDrawdown.byMonthIndex.get(months[months.length - 1]?.context.monthIndex)}
              priorUtilization={poolDrawdown.priorUtilization}
              labels={presentation.drawnCellLabels}
            />
          ),
        },
        {
          key: 'death-benefit',
          label: 'Death benefit balance',
          tooltip: presentation.copy?.deathBenefitTooltip ?? DEFAULT_DEATH_BENEFIT_TOOLTIP,
          render: ({ month, openState }) => (
            <BalanceWithDeltaCell
              opening={openState.pools.deathBenefit}
              closing={month.state.pools.deathBenefit}
              tooltipContent={
                presentation.deathBenefitCellTooltip?.({ month, openState, terms }) ??
                undefined
              }
            />
          ),
          renderAggregate: ({ openStateAtStart, closeStateAtEnd, kind }) => (
            <BalanceWithDeltaCell
              opening={
                kind === 'total'
                  ? closeStateAtEnd.pools.deathBenefit
                  : openStateAtStart.pools.deathBenefit
              }
              closing={closeStateAtEnd.pools.deathBenefit}
            />
          ),
        },
      ],
    },
  ];

  const extras = presentation.extraPayoutColumnGroups?.({ terms, yearSnapshots }) ?? [];
  const hidden = new Set<string>(presentation.hiddenColumns ?? []);
  // Filter hidden columns *and* drop any column group left empty so we don't
  // render a phantom group separator.
  const filtered = [...universalGroups, ...extras]
    .map(group => ({ ...group, columns: group.columns.filter(c => !hidden.has(c.key)) }))
    .filter(group => group.columns.length > 0);
  return filtered;
}
